import { ROUTE_PRODUCT } from '@constants';
import { EventTriggerSource, TrackEvent } from '@enums';
import { FlowModuleFragment } from '@graphql/generated/graphql';
import { useStep } from '@hooks';
import { useAnalytics } from '@lib/analytics/analyticsContext';
import { useRouter } from 'next/router';
import {
    createContext,
    FC,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { isRegistrationFlowPage } from '../pages/utils/validators';
import { Content } from '../types';
import { getContentTypeAndSlug } from '../utils/getters';
import {
    isContentFlowPage,
    isContentSuccessFlowPage,
} from '../utils/validators';

interface IFlowContext {
    triggerSource: EventTriggerSource;
    module: FlowModuleFragment;
    currentContent?: Content;
    currentStep: number;
    totalSteps: number;
    isRedirecting: boolean;
    isFlowCompleted: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onSkip: () => void;
    onExit?: () => void;
}

export const FlowContext = createContext<IFlowContext>({} as IFlowContext);

interface Props {
    children: ReactNode;
    module: FlowModuleFragment;
    triggerSource: EventTriggerSource;
    onExit?: () => void;
}

export const FlowProvider: FC<Props> = ({
    children,
    module,
    triggerSource,
    onExit,
}) => {
    const {
        events: routerEvents,
        locale,
        push: routerPush,
        query,
    } = useRouter();
    const { track } = useAnalytics();
    const [isRedirecting, setIsRedirecting] = useState<boolean>(false);

    const contents = useMemo(() => module.Content, [module.Content]);
    const totalSteps = useMemo(() => contents?.length || 0, [contents]);

    const [
        currentStep,
        { canGoToNextStep, setStep, goToPrevStep, goToNextStep },
    ] = useStep(totalSteps);

    const currentContent = useMemo(
        () => contents?.[currentStep - 1],
        [contents, currentStep]
    );

    const isFlowCompleted = useMemo(
        () => currentStep === totalSteps,
        [currentStep, totalSteps]
    );

    const trackFlowContentView = useCallback((): void => {
        const { content_type, content_slug } =
            getContentTypeAndSlug(currentContent);

        track(TrackEvent.FlowContentView, {
            flow_UID: module.UID || '',
            step_number: currentStep,
            total_steps: totalSteps,
            content_type,
            content_slug,
            trigger_source: triggerSource,
        });
    }, [
        track,
        currentStep,
        currentContent,
        totalSteps,
        module.UID,
        triggerSource,
    ]);

    const trackFlowCompleted = useCallback((): void => {
        track(TrackEvent.FlowCompleted, {
            flow_UID: module.UID || '',
            total_steps: totalSteps,
            trigger_source: triggerSource,
        });
    }, [track, totalSteps, module.UID, triggerSource]);

    const trackFlowDropOff = useCallback((): void => {
        const { content_type, content_slug } =
            getContentTypeAndSlug(currentContent);

        track(TrackEvent.FlowDropOff, {
            flow_UID: module.UID || '',
            step_number: currentStep,
            total_steps: totalSteps,
            content_type,
            content_slug,
            trigger_source: triggerSource,
        });
    }, [
        track,
        currentStep,
        currentContent,
        totalSteps,
        module.UID,
        triggerSource,
    ]);

    useEffect(() => {
        trackFlowContentView();
    }, [currentStep, trackFlowContentView]);

    useEffect(() => {
        const handleRouteChange = (): void => {
            if (isFlowCompleted) {
                return trackFlowCompleted();
            }

            trackFlowDropOff();
        };

        routerEvents.on('routeChangeStart', handleRouteChange);
        return () => {
            routerEvents.off('routeChangeStart', handleRouteChange);
        };
    }, [routerEvents, isFlowCompleted, trackFlowCompleted, trackFlowDropOff]);

    const onLastStep = useCallback((): Promise<boolean> => {
        onExit?.();
        setIsRedirecting(true);
        return routerPush(
            {
                pathname: ROUTE_PRODUCT,
                query,
            },
            `/${locale}/${query.product}`
        );
    }, [locale, query, routerPush, onExit]);

    const onNext = useCallback(async (): Promise<boolean | void> => {
        if (canGoToNextStep) {
            return goToNextStep();
        }

        return onLastStep();
    }, [canGoToNextStep, goToNextStep, onLastStep]);

    const findSkipDestination = useCallback((): number | undefined => {
        if (!contents) return;

        const skipDestination = contents.find((component, index) => {
            if (index > currentStep - 1) {
                if (
                    isContentFlowPage(currentContent) &&
                    currentContent.FlowPage &&
                    isRegistrationFlowPage(currentContent.FlowPage)
                ) {
                    return !isContentSuccessFlowPage(component);
                }

                return true;
            }

            return false;
        });

        if (skipDestination) {
            return contents?.indexOf(skipDestination) + 1;
        }
    }, [contents, currentStep, currentContent]);

    const onSkip = useCallback((): void => {
        const nextStep = findSkipDestination();

        if (nextStep) {
            return setStep(nextStep);
        }

        onLastStep();
    }, [findSkipDestination, setStep, onLastStep]);

    return (
        <FlowContext.Provider
            value={{
                triggerSource,
                module,
                currentContent,
                currentStep,
                totalSteps,
                isRedirecting,
                isFlowCompleted,
                onPrevious: goToPrevStep,
                onNext,
                onSkip,
                onExit,
            }}
        >
            {children}
        </FlowContext.Provider>
    );
};

export const useFlow = () => useContext(FlowContext);
